import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { AnimatedPasswordCheck } from "../components/PasswordCheck.tsx";
import { isStrongPassword } from "../../../src/shared";
import { useSnackbar } from 'notistack';
import { fetchWithError } from "../utils/fetchWithError.ts";
import { InAppContainer } from "../container/InAppContainer.tsx";

export const ChangePasswordScreen = () => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [passwordRepeat, setPasswordRepeat] = useState("");
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // Check current password
            const testCurrent = await fetchWithError<{ ok: boolean }>("/session/check-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    password: currentPassword
                })
            });

            if (!testCurrent.ok) {
                enqueueSnackbar("Das aktuelle Passwort ist falsch!", { variant: 'error' });
                return;
            }

            // Validate new password
            const pwd = isStrongPassword(newPassword);
            if (!pwd.isStrongPassword) {
                setShowPasswordHint(true);
                enqueueSnackbar("Das neue Passwort erfüllt nicht die Anforderungen!", { variant: 'warning' });
                return;
            }

            if (newPassword !== passwordRepeat) {
                enqueueSnackbar("Die neuen Passwörter stimmen nicht überein!", { variant: 'error' });
                return;
            }

            // Change password
            const response = await fetchWithError<{ success: boolean }>("/session/change-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    newPassword,
                    currentPassword
                })
            });

            if (response.success) {
                enqueueSnackbar("Dein Passwort wurde erfolgreich geändert!", { variant: 'success' });
                navigate("/app/profile");
            }
        } catch (error) {
            // Display the specific error message from the backend
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie den Vorgang erneut.", { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <InAppContainer>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 2, sm: 4 },
                    width: '100%',
                    maxWidth: '500px',
                    mx: 'auto'
                }}
            >
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Passwort ändern
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                    Ändere dein Passwort, um dein Konto sicher zu halten.
                </Typography>
                <Stack spacing={3} sx={{ width: '100%' }}>
                    <TextField
                        label="Aktuelles Passwort"
                        variant="outlined"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                    >
                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label="Neues Passwort"
                        variant="outlined"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (!showPasswordHint) {
                                setShowPasswordHint(true);
                            }
                        }}
                        required
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <AnimatedPasswordCheck
                        show={showPasswordHint && !isStrongPassword(newPassword).isStrongPassword}
                        password={newPassword}
                    />
                    <TextField
                        label="Neues Passwort wiederholen"
                        variant="outlined"
                        type={showRepeatPassword ? "text" : "password"}
                        value={passwordRepeat}
                        onChange={(e) => setPasswordRepeat(e.target.value)}
                        required
                        fullWidth
                        error={newPassword !== passwordRepeat && passwordRepeat.length > 0}
                        helperText={newPassword !== passwordRepeat && passwordRepeat.length > 0 ? "Passwörter stimmen nicht überein" : ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                                        edge="end"
                                    >
                                        {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Stack>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading}
                    sx={{ mt: 4, width: '100%' }}
                >
                    {isLoading ? "Speichern..." : "Passwort speichern"}
                </Button>
                <Button
                    variant="text"
                    onClick={() => navigate('/app/profile')}
                    startIcon={<ArrowBack />}
                    sx={{ mt: 2 }}
                >
                    Zurück zum Profil
                </Button>
            </Box>
        </InAppContainer>
    );
};